import React, { useState, useEffect } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Helmet } from "react-helmet-async";
const OracleEnterpriseSolutions = () => {
  const phrases = [
    "Autonomous Database Tuning",
    "OCI Bare Metal Scaling",
    "Fusion Cloud Implementations"
  ];

  const cardData = [
    { type: 'oracle-core', content: '💾' },
    { type: 'copper-wave', content: '📊' },
    { type: 'oci-stack', content: '⚙️' },
    { type: 'fusion-spark', content: '⚡' }
  ];

  const [currentPhraseIndex, setCurrentPhraseIndex] = useState(0);
  const [displayedText, setDisplayedText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [cardIndex, setCardIndex] = useState(0);

  useEffect(() => {
    const currentPhrase = phrases[currentPhraseIndex];
    let timer;

    const handleTyping = () => {
      if (!isDeleting) {
        setDisplayedText(currentPhrase.substring(0, displayedText.length + 1));
        if (displayedText === currentPhrase) {
          timer = setTimeout(() => setIsDeleting(true), 1600);
          return;
        }
      } else {
        setDisplayedText(currentPhrase.substring(0, displayedText.length - 1));
        if (displayedText === '') {
          setIsDeleting(false);
          setCurrentPhraseIndex((prev) => (prev + 1) % phrases.length);
          return;
        }
      }

      const speed = isDeleting ? 30 : 80;
      timer = setTimeout(handleTyping, speed);
    };

    timer = setTimeout(handleTyping, 100);
    return () => clearTimeout(timer);
  }, [displayedText, isDeleting, currentPhraseIndex]);

  useEffect(() => {
    const cardTimer = setInterval(() => {
      setCardIndex((prev) => (prev + 1) % cardData.length);
    }, 1100);
    return () => clearInterval(cardTimer);
  }, []);

  // Intersection Observer for Scroll Animations
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.15
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-visible');
        }
      });
    }, observerOptions);

    const animatedElements = document.querySelectorAll(
      '.scroll-anim-fade-in, .scroll-anim-fade-in-up, .scroll-anim-fade-in-left, .scroll-anim-fade-in-right, .scroll-anim-flip, .scroll-anim-zoom-in'
    );

    animatedElements.forEach((el) => observer.observe(el));

    return () => {
      animatedElements.forEach((el) => observer.unobserve(el));
    };
  }, []);

  // Content Data
  const coreServices = [
    { icon: "💾", title: "Autonomous Database Architecture", desc: "Deploy, tune, and maintain self-repairing Oracle Autonomous Databases, maximizing indexing efficiency while eliminating manual optimization overhead." },
    { icon: "⚙️", title: "Oracle Fusion Cloud ERP & SCM", desc: "Streamline cross-border financial consolidation pipelines and multi-tier procurement ledger paths through custom-tailored Oracle Fusion instances." },
    { icon: "⚡", title: "OCI Compute & Bare Metal Provisioning", desc: "Architect low-latency cloud infrastructure networks featuring custom VCN layouts, private subnet groups, and raw bare metal performance profiles." },
    { icon: "📊", title: "High-Availability Real Application Clusters (RAC)", desc: "Build bulletproof database clustering matrices with zero-single-point-of-failure architectures to maintain 24/7 transaction uptime." },
    { icon: "🔄", title: "GoldenGate Real-Time Data Streaming", desc: "Configure high-speed log-based change data capture (CDC) pipelines for zero-downtime cross-platform database replication and continuous ledger updates." },
    { icon: "🛡️", title: "Database Security & Advanced Encryption", desc: "Enforce granular Transparent Data Encryption (TDE), rigid Oracle Data Vault separation of duties, and continuous audit security logs." },
    { icon: "📐", title: "PL/SQL Programmatic Engine Refactoring", desc: "Clean and modularize heavy legacy database procedures, compound package triggers, and batch computing structures for peak response speed." }
  ];

  const advancedServices = [
    { icon: "🔍", title: "E-Business Suite (EBS) Lift-and-Shift", desc: "Systematically migrate legacy on-premise EBS deployments into highly scalable, performant OCI environments with zero ledger state loss." },
    { icon: "💼", title: "Oracle Analytics Server (OAS) Pipelines", desc: "Synthesize high-volume corporate ledger entries into interactive, real-time enterprise performance dashboard metrics." },
    { icon: "🗃️", title: "Exadata Cloud@Customer Coordination", desc: "Bring high-velocity database hardware compute processing directly inside private corporate data boundaries for strict compliance protocols." },
    { icon: "⚖️", title: "Multi-Tenant Container Architecture", desc: "Consolidate hundreds of isolated database nodes safely using pluggable database (PDB) schemas to significantly reduce infrastructure cost layers." },
    { icon: "🚨", title: "Enterprise Manager (OEM) Grid Control", desc: "Configure real-time proactive monitoring matrices to capture query execution variations before operational drops occur." },
    { icon: "📝", title: "WebLogic Server & Java Middleware Tuning", desc: "Optimize high-throughput corporate middleware transaction pools, garbage collection parameters, and secure cluster routing lines." },
    { icon: "🌐", title: "REST Data Services (ORDS) Endpoints", desc: "Expose complex internal relational query tables securely as fast, language-agnostic modern microservice API channels." },
    { icon: "🧪", title: "Real Application Testing (RAT) Verification", desc: "Simulate massive live production transaction workloads over test targets to validate system stability before running minor patch updates." },
    { icon: "📦", title: "Hyper-Converged Disaster Recovery Plans", desc: "Design completely automated Data Guard switchover sequences ensuring real-time multi-region transactional validation loops." }
  ];

  const technologies = {
    "Core Relational Engines": ["Oracle Database 23c / 19c / 12c", "Autonomous Data Warehouse (ADW)", "Autonomous Transaction Processing", "Exadata Database Machine Platforms"],
    "Cloud Infrastructure OCI": ["OCI Compute & Bare Metal Instances", "OCI Virtual Cloud Networks (VCN)", "Oracle Cloud VMware Solution", "OCI Identity & Access Management"],
    "Enterprise Software Suites": ["Oracle Fusion Cloud ERP / HCM", "E-Business Suite (EBS) R12.2", "Oracle PeopleSoft / JD Edwards", "Oracle NetSuite Cloud Architectures"],
    "Integration & Pipelines": ["Oracle GoldenGate Replication Engine", "Oracle Data Integrator (ODI)", "Oracle REST Data Services (ORDS)", "Oracle Managed File Transfer (MFT)"],
    "Performance & Safety": ["Oracle Real Application Clusters (RAC)", "Oracle Active Data Guard Failovers", "Oracle Real Application Testing (RAT)", "Oracle Advanced Security (TDE/Vault)"]
  };

  const processSteps = [
    { num: "01", title: "Database & Structure Discovery", desc: "Analyzing schema complexity, tracing fragment configurations, and mapping out structural table dependency chains." },
    { num: "02", title: "OCI & Storage Target Blueprinting", desc: "Sizing memory allocations, planning IOPS compute structures, and structuring virtual cloud networking firewalls." },
    { num: "03", title: "GoldenGate Ingestion & Syncing", desc: "Setting up real-time capture profiles, running source table initial loads, and matching transactional checkpoints." },
    { num: "04", title: "PL/SQL Optimization & Patching", desc: "Refactoring slow table joins, modernizing index partition parameters, and deploying clean programmatic procedures." },
    { num: "05", title: "Failover Validation & Stress Sprints", desc: "Executing simulated network drops, verifying RAC cluster state routing, and recording maximum throughput metrics." },
    { num: "06", title: "System Cutover & Active Switch", desc: "Running final operational reconciliation passes and moving production app connection lines to the new nodes seamlessly." },
    { num: "07", title: "Continuous Tuning Loop Monitoring", desc: "Reviewing execution logs via AWR reports, tuning cache parameters, and adjusting automated optimization routines." }
  ];

  const industries = [
    "High-Volume Global Financial & Clearing Systems", "Cross-Border Enterprise Supply & Logistics Networks", "High-Density Energy Utilities & Power Distribution",
    "Global Pharmaceutical & Clinical Research Operations", "Aerospace, Heavy Defense & Vehicle Manufacturing", "Telecommunications Multi-Region Billing Carriers",
    "Public Sector Central Government Registries", "High-Velocity Commodity & Trade Settlements Platforms", "Commercial Insurance Claim Processing Environments",
    "Multi-Campus University Systems & Education Hubs", "High-Scale E-Commerce Transactions & Billing Platforms", "Global Mining & Resource Extraction Infrastructures"
  ];

  const designBenefits = [
    "Zero Financial Transactional Reconciliation Variances", "Up to 65% Reduction in Query Execution Latencies", "100% Secure, Encrypted Cloud Database Infrastructure",
    "Guaranteed 99.999% Database Cluster Transactional Uptime", "Drastic Drop in Manual Database Administration Timeframes", "Highly Optimized Multi-Tenant Schema Configurations",
    "Seamless Zero-Downtime Data Migration Architecture Paths", "Real-Time Enterprise Performance Analytics Ingestion Lines"
  ];

  const whyChooseUs = [
    "Certified Oracle Solutions & Cloud Infrastructure Architects", "Proven Experience Coordinating High-Volume Migrations", "Airtight Data Vault & Advanced Security Governance Models",
    "Deep Competency in Complex PL/SQL Performance Optimization", "Platform-Agnostic Hybrid Infrastructure Integration Expertise", "Resilient High-Availability RAC Cluster Configurations",
    "End-to-End Environment Transport & Lifespan Governance", "Transparent Architectural Goals & Verifiable Testing Metrics", "High-Availability Database Ingestion Infrastructure SLAs",
    "Experienced GoldenGate Streaming & ODI Architecture Specialists", "Iterative Proof-of-Concept Target Environment Testing Sprints", "100% Relational Transaction Ledger Durability Assurances"
  ];

  return (
    <>
      <Helmet>
        {/* Primary SEO */}
        <title>
          Oracle Cloud & Database Solutions | Oracle Consulting & Cloud Services | Affsoft IT Solutions Pvt. Ltd.
        </title>

        <meta
          name="title"
          content="Oracle Cloud & Database Solutions | Oracle Consulting & Cloud Services | Affsoft IT Solutions Pvt. Ltd."
        />

        <meta
          name="description"
          content="Affsoft IT Solutions Pvt. Ltd. provides Oracle Cloud and Database Solutions, including Oracle Cloud Infrastructure (OCI), Oracle Database administration, Oracle Cloud migration, database optimization, Oracle ERP, Oracle integration, managed services, and enterprise cloud solutions."
        />

        <meta
          name="keywords"
          content="Oracle Cloud, Oracle Cloud Services, Oracle Database, Oracle Database Solutions, Oracle Cloud Infrastructure, OCI, Oracle Cloud Migration, Oracle Database Administration, Oracle ERP, Oracle Integration, Oracle Managed Services, Oracle Cloud Consulting, Database Optimization, Enterprise Database Solutions, Oracle Cloud Implementation, Oracle Cloud Solutions, Affsoft IT Solutions Pvt. Ltd."
        />

        <meta name="robots" content="index, follow" />
        <meta name="author" content="Affsoft IT Solutions Pvt. Ltd." />
        <meta name="language" content="English" />
        <meta name="theme-color" content="#0d5c53" />

        {/* Canonical */}
        <link
          rel="canonical"
          href="https://www.affsoft.com/oracle-cloud-and-database-solutions"
        />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta
          property="og:site_name"
          content="Affsoft IT Solutions Pvt. Ltd."
        />
        <meta
          property="og:title"
          content="Professional Oracle Cloud & Database Solutions | Affsoft IT Solutions Pvt. Ltd."
        />
        <meta
          property="og:description"
          content="Modernize your enterprise with Oracle Cloud and Database Solutions from Affsoft IT Solutions Pvt. Ltd. We specialize in Oracle Cloud Infrastructure, database administration, cloud migration, Oracle ERP, integration, and managed services."
        />
        <meta
          property="og:url"
          content="https://www.affsoft.com/oracle-cloud-and-database-solutions"
        />
        <meta
          property="og:image"
          content="https://www.affsoft.com/images/affsoft-logo.png"
        />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Oracle Cloud & Database Solutions | Oracle Consulting & Cloud Services | Affsoft IT Solutions Pvt. Ltd."
        />
        <meta
          name="twitter:description"
          content="Optimize your enterprise with Oracle Cloud and Database Solutions from Affsoft IT Solutions Pvt. Ltd. We deliver Oracle Cloud Infrastructure, database management, cloud migration, Oracle ERP, and enterprise database services."
        />
        <meta
          name="twitter:image"
          content="https://www.affsoft.com/images/affsoft-logo.png"
        />
      </Helmet>
      <div className="oracle-services-page bg-light" style={{ fontFamily: '"Inter", sans-serif', overflowX: 'hidden' }}>

        {/* ================= HERO SECTION ================= */}
        <div
          className="hero-container container-fluid d-flex align-items-center justify-content-center position-relative overflow-hidden"
          style={{
            backgroundImage: "url('./images/home.jpg')",
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            minHeight: '85vh',
            backgroundColor: '#1b1715' // Deep Redwood/Enterprise charcoal fallback
          }}
        >
          {/* Background Grid Lines */}
          <div className="grid-overlay position-absolute top-0 start-0 w-100 h-100" style={{ opacity: 0.12 }}>
            <div className="grid-line line-v-1"></div>
            <div className="grid-line line-v-2"></div>
            <div className="grid-line line-v-3"></div>
          </div>

          {/* Infinite Marquee Text */}
          <div className="marquee-wrapper select-none position-absolute w-100 scroll-anim-fade-in" style={{ top: '15%', opacity: 0.05 }}>
          </div>

          <div className="marquee-wrapper select-none position-absolute w-100 scroll-anim-fade-in">
            <div className="marquee-content text-white">
              <span>Oracle &nbsp; Cloud &nbsp; Infrastructure &nbsp; &amp; &nbsp;</span>
              <span>Autonomous Database Architectures </span>
            </div>
          </div>

          {/* Main Content Layout */}
          <div className="container position-relative z-3 my-5">
            <div className="row align-items-center py-4">

              {/* Left Side Info */}
              <div className="col-12 col-md-5 text-start d-flex flex-column align-items-start hero-left-pane text-white mb-4 mb-md-0 scroll-anim-fade-in-left">
                <div className="asterisk-icon mb-3 text-copper fs-3" style={{ color: '#c76a44' }}>✳</div>
                <h1 className="fw-bold mb-3 display-5">Oracle Cloud &amp; Systems</h1>
                <p className="tagline mb-4 text-white" style={{ fontSize: '1rem', lineHeight: '1.6', opacity: 0.9 }}>
                  At Affsoft IT Solutions Pvt. Ltd., we eliminate the performance bottlenecks that limit large-scale transactional workloads. Our Oracle systems engineering practice pairs resilient Autonomous Database optimization with high-availability RAC clustering and secure OCI infrastructure layouts to convert heavy enterprise data hubs into performant strategic spaces.
                </p>
                <button className="btn btn-outline-light d-flex align-items-center gap-2 px-4 py-2 rounded-pill fw-semibold">
                  <span>➔</span>
                  <span>Scope an Oracle Architecture</span>
                </button>
              </div>

              {/* Center Section */}
              <div className="col-12 col-md-4 text-center mb-4 mb-md-0 hero-center-pane scroll-anim-flip">
                <div className="typing-badge px-4 py-3 mx-auto bg-dark bg-opacity-75 rounded-pill border border-secondary shadow-lg d-inline-block">
                  <span className="typed-text text-white fw-mono fs-4">{displayedText}</span>
                  <span className="cursor fs-4 animate-blink" style={{ color: '#c76a44' }}>|</span>
                </div>
              </div>

              {/* Right Section */}
              <div className="col-12 col-md-3 d-flex justify-content-center justify-content-md-end hero-right-pane scroll-anim-fade-in-right">
                <div className="glass-card-frame shadow-lg p-3 bg-white bg-opacity-10 rounded-4 border border-light" style={{ width: '220px', backdropFilter: 'blur(10px)' }}>
                  <div className={`inner-graphic-card graphic-${cardData[cardIndex].type} d-flex align-items-center justify-content-center rounded-3 bg-dark`} style={{ height: '180px' }}>
                    <span className="graphic-icon display-3">{cardData[cardIndex].content}</span>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>

        {/* ================= CORE SOLUTIONS SECTION ================= */}
        <section className="py-5 bg-white">
          <div className="container">
            <div className="text-center max-width-700 mx-auto mb-5 scroll-anim-fade-in-up">
              <span className="badge px-3 py-2 rounded-pill mb-2 text-uppercase text-white" style={{ backgroundColor: '#c76a44' }}>Database Architecture Scaling</span>
              <h2 className="fw-bold text-dark display-6">Deploy High-Throughput Compute Pools & Real-Time Replication Layers</h2>
              <p className="text-muted mt-3">
                Unoptimized PL/SQL execution paths, single-point-of-failure storage clusters, and sluggish cross-system transaction replication loops create risky operational friction. At Affsoft IT Solutions Pvt. Ltd., we construct airtight, zero-downtime, and systematically tracked relational infrastructure frameworks.
              </p>
            </div>

            <div className="row g-4">
              {coreServices.map((service, index) => (
                <div className="col-12 col-md-6 col-lg-4 scroll-anim-zoom-in" key={index} style={{ animationDelay: `${index * 0.1}s` }}>
                  <div className="card h-100 shadow-sm border-0 rounded-4 p-4 hover-translate transition-all">
                    <div className="fs-1 mb-3" style={{ color: '#c76a44' }}>{service.icon}</div>
                    <h4 className="fw-bold fs-5 mb-2 text-dark">{service.title}</h4>
                    <p className="text-muted mb-0 small">{service.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Business Value Checklist */}
            <div className="mt-5 p-4 rounded-4 bg-light border scroll-anim-fade-in-up">
              <h5 className="fw-bold text-center text-dark mb-4">Core Operational Gains of Strategic Enterprise Oracle Modernization</h5>
              <div className="row g-3">
                {designBenefits.map((benefit, index) => (
                  <div className="col-6 col-md-3 d-flex align-items-center gap-2" key={index}>
                    <span className="text-success fs-5">✓</span>
                    <span className="text-secondary small fw-medium">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ================= ADVANCED SOLUTIONS OPTIONS ================= */}
        <section className="py-5 bg-light">
          <div className="container">
            <div className="text-center max-width-700 mx-auto mb-5 scroll-anim-fade-in-up">
              <span className="badge px-3 py-2 rounded-pill mb-2 text-uppercase text-white" style={{ backgroundColor: '#c76a44' }}>Distributed System Nodes</span>
              <h2 className="fw-bold text-dark display-6">Adaptive Core Capabilities Built for Massive IOPS Demands</h2>
              <p className="text-muted mt-3">
                We scale rigid baseline database nodes into dynamic, self-tuning transactional ecosystems. From managing hybrid Exadata deployments to configuring secure ORDS microservice routes, our implementations guarantee absolute relational durability.
              </p>
            </div>

            <div className="row g-4">
              {advancedServices.map((service, index) => (
                <div className="col-12 col-md-6 col-lg-4 scroll-anim-zoom-in" key={index} style={{ animationDelay: `${index * 0.1}s` }}>
                  <div className="card h-100 shadow-sm border-0 rounded-4 p-4 bg-white hover-translate transition-all">
                    <div className="fs-1 mb-3" style={{ color: '#c76a44' }}>{service.icon}</div>
                    <h4 className="fw-bold fs-5 mb-2 text-dark">{service.title}</h4>
                    <p className="text-muted mb-0 small">{service.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= TECHNOLOGIES SECTION ================= */}
        <section className="py-5 bg-dark text-white">
          <div className="container">
            <div className="text-center mb-5 scroll-anim-fade-in-up">
              <span className="badge px-3 py-2 rounded-pill mb-2 text-uppercase fw-bold text-dark" style={{ backgroundColor: '#f3a27c' }}>Oracle Ecosystem Stack</span>
              <h2 className="fw-bold display-6">Relational Systems & Compute Frameworks We Standardize</h2>
              <p className="text-light-50">We configure, interface, and maintain enterprise operational networks utilizing strict validation matrices.</p>
            </div>

            <div className="row g-4 justify-content-center">
              {Object.entries(technologies).map(([category, list], index) => (
                <div className="col-12 col-sm-6 col-md-4 col-lg scroll-anim-fade-in" key={index}>
                  <div className="bg-secondary bg-opacity-25 p-4 rounded-4 h-100 border border-secondary border-opacity-50">
                    <h5 className="fw-bold mb-3 border-bottom border-secondary pb-2" style={{ color: '#f3a27c' }}>{category}</h5>
                    <ul className="list-unstyled mb-0">
                      {list.map((tech, i) => (
                        <li key={i} className="mb-2 d-flex align-items-center gap-2 small">
                          <span className="opacity-75" style={{ color: '#f3a27c' }}>•</span> {tech}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= ROADMAP PROCESS ================= */}
        <section className="py-5 bg-white">
          <div className="container">
            <div className="text-center mb-5 scroll-anim-fade-in-up">
              <span className="badge bg-secondary px-3 py-2 rounded-pill mb-2 text-uppercase">Migration Lifecycle</span>
              <h2 className="fw-bold text-dark display-6">Our Engineered Architecture Blueprint</h2>
              <p className="text-muted">A structured, strictly governed development lifecycle designed to transition heavy core processes safely from validation passes onto active live production clusters.</p>
            </div>

            <div className="row g-3">
              {processSteps.map((step, index) => (
                <div className="col-12 col-md-6 col-lg scroll-anim-fade-in-up" key={index} style={{ animationDelay: `${index * 0.15}s` }}>
                  <div className="p-4 rounded-4 bg-light border-start border-4 h-100 shadow-sm" style={{ borderLeftColor: '#c76a44' }}>
                    <div className="fw-bold display-6 mb-2" style={{ color: '#c76a44' }}>{step.num}</div>
                    <h5 className="fw-bold text-dark fs-6">{step.title}</h5>
                    <p className="text-muted small mb-0">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= INDUSTRIES SERVED ================= */}
        <section className="py-5 bg-light">
          <div className="container text-center">
            <h2 className="fw-bold text-dark mb-4 display-6 scroll-anim-fade-in-up">Ecosystems We Empower</h2>
            <div className="d-flex flex-wrap justify-content-center gap-2 max-width-900 mx-auto scroll-anim-zoom-in">
              {industries.map((industry, index) => (
                <span className="badge bg-white text-dark border px-3 py-2 rounded-pill shadow-sm fs-7 fw-medium" key={index}>
                  🏢 {industry}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* ================= WHY CHOOSE AFFSOFT ================= */}
        <section className="py-5 bg-white border-top">
          <div className="container">
            <div className="text-center mb-5 scroll-anim-fade-in-up">
              <h2 className="fw-bold text-dark display-6">Why Partner with Affsoft IT Solutions Pvt. Ltd.?</h2>
              <p className="text-muted">We combine deep relational mastery with hardware-level infrastructure visibility to ensure highly predictable transaction behavior.</p>
            </div>

            <div className="row g-3">
              {whyChooseUs.map((reason, index) => (
                <div className="col-12 col-md-6 col-lg-4 scroll-anim-fade-in-left" key={index} style={{ animationDelay: `${(index % 3) * 0.1}s` }}>
                  <div className="d-flex align-items-center gap-3 p-3 bg-light rounded-3 h-100">
                    <span className="fs-4" style={{ color: '#c76a44' }}>🛡️</span>
                    <span className="text-dark fw-semibold small">{reason}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Custom Styles Injection */}
        <style>{`
        .hover-translate {
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }
        .hover-translate:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 20px rgba(0,0,0,0.1) !important;
        }
        .text-light-50 {
          color: rgba(255, 255, 255, 0.7);
        }
        .max-width-700 {
          max-width: 700px;
        }
        .max-width-900 {
          max-width: 900px;
        }
        .animate-blink {
          animation: blink 1s step-end infinite;
        }
        @keyframes blink {
          from, to { color: transparent }
          50% { color: inherit }
        }

        /* --- Scroll Animation Core --- */
        .scroll-anim-fade-in,
        .scroll-anim-fade-in-up,
        .scroll-anim-fade-in-left,
        .scroll-anim-fade-in-right,
        .scroll-anim-flip,
        .scroll-anim-zoom-in {
          opacity: 0;
          will-change: transform, opacity;
          transition: all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94);
        }

        /* Visibility Trigger via IntersectionObserver */
        .animate-visible {
          opacity: 1 !important;
          transform: none !important;
        }

        /* --- Animations Configurations --- */
        .scroll-anim-fade-in-up {
          transform: translateY(40px);
        }
        .scroll-anim-fade-in-left {
          transform: translateX(-50px);
        }
        .scroll-anim-fade-in-right {
          transform: translateX(50px);
        }
        .scroll-anim-zoom-in {
          transform: scale(0.85);
        }
        .scroll-anim-flip {
          transform: perspective(400px) rotateY(90deg);
          transition: all 1s cubic-bezier(0.175, 0.885, 0.32, 1.275);
        }
        
        .scroll-anim-flip.animate-visible {
          transform: perspective(400px) rotateY(0deg) !important;
        }
      `}</style>

      </div>
    </>
  );
};

export default OracleEnterpriseSolutions;